import { useServices } from '@/app/providers';
import { useI18n } from '@/shared/i18n';
import type { PaginatedAccessLogs } from '@yawi/domain';
import type { AccessLog, AccessStatus, AccessType } from '@yawi/types';
import type { CreateGuardAccessLogDTO } from '@yawi/types';
import { useCallback, useEffect, useRef, useState } from 'react';

interface AccessLogsParams {
  page: number;
  limit: number;
  method?: AccessType;
  status?: AccessStatus;
  date?: string;
  entryPoint?: string;
  personSearch?: string;
  detailSearch?: string;
}

const TEXT_DEBOUNCE_MS = 400;

const normalizeLogValue = (value: unknown): string => (value == null ? '' : String(value).trim().toLowerCase());

function getAccessLogFingerprint(log: AccessLog): string {
  return [
    log.timestamp,
    log.userName,
    log.entryPoint,
    log.status,
    log.details,
    log.plateNumber,
  ].map(normalizeLogValue).join('::');
}

function dedupAccessLogs(logs: AccessLog[]): AccessLog[] {
  const seen = new Set<string>();
  const result: AccessLog[] = [];

  for (const log of logs) {
    const fingerprint = getAccessLogFingerprint(log);
    if (seen.has(fingerprint)) {
      continue;
    }

    seen.add(fingerprint);
    result.push(log);
  }

  return result;
}

export function useAccessLogs(initialParams?: AccessLogsParams) {
  const { accessLogRepo, guardRepo } = useServices();
  const [data, setData] = useState<PaginatedAccessLogs | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entryPointOptions, setEntryPointOptions] = useState<string[]>([]);
  const [params, setParams] = useState<AccessLogsParams>(initialParams || { page: 1, limit: 20 });
  const [debouncedParams, setDebouncedParams] = useState(params);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    const hasTextChange =
      params.personSearch !== debouncedParams.personSearch ||
      params.detailSearch !== debouncedParams.detailSearch;

    if (!hasTextChange) {
      setDebouncedParams(params);
      return;
    }

    const timer = setTimeout(() => setDebouncedParams(params), TEXT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [params]);

  const fetchEntryPointOptions = useCallback(async () => {
    try {
      const result = await accessLogRepo.getLogsPaginated(undefined, 1, 100);
      const points = new Set(result.data.map((l) => l.entryPoint).filter(Boolean) as string[]);
      setEntryPointOptions(Array.from(points).sort());
    } catch (err) {
      console.error('[useAccessLogs] entry point options', err);
    }
  }, [accessLogRepo]);

  const fetchAccessLogs = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setError(null);
    try {
      const p = debouncedParams;
      const startDate = p.date ? new Date(`${p.date}T00:00:00`).toISOString() : undefined;
      const endDate = p.date ? new Date(`${p.date}T23:59:59`).toISOString() : undefined;

      const result = await accessLogRepo.getLogsPaginated(
        {
          method: p.method,
          status: p.status,
          startDate,
          endDate,
          entryPoint: p.entryPoint,
          search: p.personSearch,
          detailSearch: p.detailSearch,
        },
        p.page,
        p.limit,
        signal,
      );
      if (signal.aborted) return;

      const deduped = dedupAccessLogs(result.data);

      setData({
        ...result,
        data: deduped,
        total: result.total,
      });
    } catch (err) {
      if (signal.aborted) return;
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [accessLogRepo, debouncedParams]);

  useEffect(() => {
    fetchAccessLogs();
    return () => abortControllerRef.current?.abort();
  }, [fetchAccessLogs]);

  useEffect(() => {
    fetchEntryPointOptions();
  }, [fetchEntryPointOptions]);

  const filterByMethod = useCallback((method: AccessType | undefined) => {
    setParams(prev => ({ ...prev, method, page: 1 }));
  }, []);

  const filterByStatus = useCallback((status: AccessStatus | undefined) => {
    setParams(prev => ({ ...prev, status, page: 1 }));
  }, []);

  const filterByDate = useCallback((date: string | undefined) => {
    setParams(prev => ({ ...prev, date, page: 1 }));
  }, []);

  const filterByEntryPoint = useCallback((entryPoint: string | undefined) => {
    setParams(prev => ({ ...prev, entryPoint, page: 1 }));
  }, []);

  const filterByPerson = useCallback((personSearch: string | undefined) => {
    setParams(prev => ({ ...prev, personSearch, page: 1 }));
  }, []);

  const filterByDetails = useCallback((detailSearch: string | undefined) => {
    setParams(prev => ({ ...prev, detailSearch, page: 1 }));
  }, []);

  const clearFilters = useCallback(() => {
    setParams(prev => ({ page: 1, limit: prev.limit }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((limit: number) => {
    setParams(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  const createAccessLog = useCallback(async (logData: CreateGuardAccessLogDTO) => {
    setCreating(true);
    setError(null);
    try {
      const newLog = await guardRepo.createAccessLog(logData);
      fetchAccessLogs();
      fetchEntryPointOptions();
      return newLog;
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      setError(message);
      throw err;
    } finally {
      setCreating(false);
    }
  }, [guardRepo, fetchAccessLogs, fetchEntryPointOptions]);

  const pagination = data
    ? {
        page: data.page,
        limit: params.limit,
        total: data.total,
        totalPages: Math.ceil(data.total / data.pageSize),
      }
    : null;

  return {
    accessLogs: data?.data ?? [],
    pagination,
    loading,
    creating,
    error,
    entryPointOptions,
    params,
    filterByMethod,
    filterByStatus,
    filterByDate,
    filterByEntryPoint,
    filterByPerson,
    filterByDetails,
    clearFilters,
    goToPage,
    setPageSize,
    createAccessLog,
    refresh: fetchAccessLogs,
  };
}
